import { useCallback, useEffect, useState } from "react";

import type { MboxHistoryView, TriageWorkbenchSubjectRef } from "./api";
import { loadMboxHistory } from "./api";
import { CoreApiError } from "../../core-api/api-client";

export type MboxHistoryLoadState =
  | { readonly status: "LOADING" }
  | { readonly status: "FAILED" }
  /** This workbench subject has no archive query; hide instead of claiming an archive failure. */
  | { readonly status: "UNSUPPORTED" }
  | { readonly status: "READY"; readonly view: MboxHistoryView };

/**
 * Loads the historical mail archive once per workbench subject.
 *
 * The workbench renders the archive in two places (customer profile facts and the product
 * reference section), so the request lives here rather than inside either section. It never
 * blocks the workbench: failures stay local to the archive surfaces.
 */
export function useMboxHistory(
  subject: TriageWorkbenchSubjectRef | undefined,
): {
  readonly state: MboxHistoryLoadState;
  readonly reload: () => void;
} {
  const [state, setState] = useState<MboxHistoryLoadState>({
    status: "LOADING",
  });
  const [reloadToken, setReloadToken] = useState(0);
  const kind = subject?.kind;
  const id = subject?.id;

  useEffect(() => {
    if (!kind || !id) {
      setState({ status: "FAILED" });
      return undefined;
    }
    const controller = new AbortController();
    let active = true;
    setState({ status: "LOADING" });
    void loadMboxHistory({ kind, id }, controller.signal)
      .then((view) => {
        if (active) setState({ status: "READY", view });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setState({
          status:
            error instanceof CoreApiError && error.status === 404
              ? "UNSUPPORTED"
              : "FAILED",
        });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [kind, id, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  return { state, reload };
}
